// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { createRequestId } from '@/services/requestId';

const DEFAULT_TIMEOUT_MS = 10000;

/**
 * Survives the Keycloak round trip. The invite token arrives in the URL fragment, and the OIDC
 * redirect replaces `window.location`, so it has to be stashed before the redirect is issued.
 */
export const FRIEND_INVITE_PENDING_KEY = 'goffy:friend-invite-pending';

export const FRIEND_INVITE_ROUTE = '/friend/invite';

const resolveFrontendBaseUrl = () => {
  const configuredFrontendBaseUrl = process.env.EXPO_PUBLIC_FRONTEND_BASE_URL;
  const webOrigin =
    typeof window !== 'undefined' && window.location?.origin ? window.location.origin : null;
  return (configuredFrontendBaseUrl ?? webOrigin ?? 'https://goffy.app').replace(/\/+$/, '');
};

/**
 * The token goes in the fragment, not the path. Fragments are never sent to a server, so the token
 * stays out of the Envoy and nginx access logs and out of the `Referer` header — unlike the tour
 * invite link, whose token is a path segment.
 */
export const buildFriendInviteRouteUrl = (inviteToken: string) =>
  `${resolveFrontendBaseUrl()}${FRIEND_INVITE_ROUTE}#${encodeURIComponent(inviteToken)}`;

/** Reads the scanned token back out of the fragment. */
export const readInviteTokenFromLocation = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const hash = window.location?.hash ?? '';
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) {
    return null;
  }
  try {
    return decodeURIComponent(raw).trim() || null;
  } catch {
    return raw.trim() || null;
  }
};

export const readPendingInviteToken = (): string | null => {
  try {
    return globalThis.sessionStorage?.getItem(FRIEND_INVITE_PENDING_KEY) ?? null;
  } catch {
    return null;
  }
};

export const storePendingInviteToken = (token: string | null) => {
  try {
    if (token) {
      globalThis.sessionStorage?.setItem(FRIEND_INVITE_PENDING_KEY, token);
    } else {
      globalThis.sessionStorage?.removeItem(FRIEND_INVITE_PENDING_KEY);
    }
  } catch {
    // Private-mode browsers and native both lack sessionStorage. The already-signed-in path still
    // works without it; only the sign-in-then-return path needs the token to survive.
  }
};

export type FriendInvitePreview = {
  inviterName: string;
  pendingToken: string;
  expiresAt: string;
};

export class FriendInviteError extends Error {
  readonly code: string;

  constructor(code: string) {
    super(code);
    this.code = code;
    this.name = 'FriendInviteError';
  }
}

/**
 * Exchanges the short-lived scanned token for a pending token that outlives a sign-in, and learns
 * who is inviting. POST with the token in the body so it never reaches a URL.
 */
export const fetchFriendInvitePreview = async ({
  apiBaseUrl,
  token,
}: {
  apiBaseUrl: string;
  token: string;
}): Promise<FriendInvitePreview> => {
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}/api/friends/invite/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
  } catch {
    throw new FriendInviteError('friend.invite.failed');
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { code?: string } | null;
    throw new FriendInviteError(
      body?.code ?? (response.status === 410 ? 'friend.invite.expired' : 'friend.invite.invalid')
    );
  }

  const payload = (await response.json()) as Partial<FriendInvitePreview>;
  if (!payload.pendingToken) {
    throw new FriendInviteError('friend.invite.invalid');
  }

  return {
    inviterName: payload.inviterName ?? '',
    pendingToken: payload.pendingToken,
    expiresAt: payload.expiresAt ?? '',
  };
};

/** Asks the BFF for a fresh QR token and returns the scannable link plus its expiry. */
export const resolveFriendInviteLink = async ({
  apiBaseUrl,
  authToken,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: {
  apiBaseUrl: string;
  authToken?: string | null;
  timeoutMs?: number;
}): Promise<{ link: string; expiresAt: string | null }> => {
  const socket = getSharedBffWebSocket(apiBaseUrl);
  socket.setAuthToken(authToken ?? null);

  const requestId = createRequestId();

  return new Promise((resolve, reject) => {
    let settled = false;
    const timeout = setTimeout(() => {
      if (settled) {
        return;
      }
      settled = true;
      unsubscribe();
      reject(new FriendInviteError('friend.invite.failed'));
    }, timeoutMs);

    const finish = (result: { value?: { link: string; expiresAt: string | null }; error?: string }) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timeout);
      unsubscribe();
      if (result.error || !result.value) {
        reject(new FriendInviteError(result.error ?? 'friend.invite.failed'));
        return;
      }
      resolve(result.value);
    };

    const unsubscribe = socket.onMessage((message) => {
      const payload = message as {
        type?: string;
        requestId?: string;
        inviteToken?: string;
        expiresAt?: string;
        code?: string;
      };
      if (!payload.type || payload.requestId !== requestId) {
        return;
      }

      if (payload.type === 'friend.invite') {
        const inviteToken = payload.inviteToken?.trim();
        finish(
          inviteToken
            ? {
                value: {
                  link: buildFriendInviteRouteUrl(inviteToken),
                  expiresAt: payload.expiresAt ?? null,
                },
              }
            : { error: 'friend.invite.failed' }
        );
        return;
      }

      if (payload.type === 'friend.invite.error') {
        finish({ error: payload.code ?? 'friend.invite.failed' });
      }
    });

    // `false` here does not mean the request was lost: the client queues anything it cannot write
    // immediately and flushes the queue when the socket opens, and `setAuthToken` above may itself
    // have closed the socket to reconnect with a changed token. Treating that as a failure showed
    // "could not create an invite code" while the request was still in flight. Let the timeout be
    // the only thing that gives up — same contract as resolveTourInviteLink.
    socket.sendFriendInviteRequest(requestId);
  });
};
